// ---- Pure tree-cutting / chop minigame sim ----------------------------------
// Pattern expansion + fail-meter judge over the shared lane primitives
// (src/sim/lane.ts — same math fishing/cooking use). Stamina helpers for the
// per-prompt spend. Server handlers + the client minigame share these; rng stays
// OUTSIDE (seed is rolled by the caller). DOM/THREE/Bun-free.

import {
  genLanePattern, startLaneJudge, applyLaneResult, forceResolveLane,
  type LanePattern, type LaneJudge, type LaneEvent, type LaneFx,
} from "./lane.ts";

// ---- Defaults (tunables doc overrides via src/tunables.ts) -------------------

export const CHOP_PROMPT_COUNT = 4;
export const CHOP_GAP = 0.85;
export const CHOP_GAP_MIN = 0.5;
export const CHOP_HIT_WINDOW = 0.2;
export const CHOP_MISS_TENSION = 0.4;
export const CHOP_HIT_RELIEF = 0.08;
export const CHOP_STA_COST = 1;
export const CHOP_INPUT_SLACK = 1.5;
export const CHOP_DEADLINE_PAD = 3;
export const CHOP_SESSION_TIMEOUT = 60;

export type ChopTuning = {
  promptCount: number;
  gap: number;
  gapMin: number;
  hitWindow: number;
  missTension: number;
  hitRelief: number;
  chopStaCost: number;
  inputSlack: number;
  deadlinePad: number;
  sessionTimeout: number;
};

export const CHOP_DEFAULTS: ChopTuning = {
  promptCount: CHOP_PROMPT_COUNT,
  gap: CHOP_GAP,
  gapMin: CHOP_GAP_MIN,
  hitWindow: CHOP_HIT_WINDOW,
  missTension: CHOP_MISS_TENSION,
  hitRelief: CHOP_HIT_RELIEF,
  chopStaCost: CHOP_STA_COST,
  inputSlack: CHOP_INPUT_SLACK,
  deadlinePad: CHOP_DEADLINE_PAD,
  sessionTimeout: CHOP_SESSION_TIMEOUT,
};

export type ChopPattern = LanePattern;
export type ChopJudge = LaneJudge;
export type ChopEvent = LaneEvent;

export function laneFx(tun: Pick<ChopTuning, "missTension" | "hitRelief">): LaneFx {
  return { miss: tun.missTension, relief: tun.hitRelief };
}

/** Expand a seed into the chop prompt pattern (both sides, identical). */
export function genChopPattern(seed: number, tun: Pick<ChopTuning, "promptCount" | "gap" | "gapMin">): ChopPattern {
  return genLanePattern(seed, tun.promptCount, tun.gap, tun.gapMin);
}

export function startChopJudge(pattern: ChopPattern, hitWindow: number): ChopJudge {
  return startLaneJudge(pattern, hitWindow);
}

export function applyChopResult(j: ChopJudge, hit: boolean, tun: Pick<ChopTuning, "missTension" | "hitRelief">): ChopEvent[] {
  return applyLaneResult(j, hit, laneFx(tun));
}

export function forceResolveChop(j: ChopJudge, tun: Pick<ChopTuning, "missTension" | "hitRelief">): ChopEvent[] {
  return forceResolveLane(j, laneFx(tun));
}

/** Enough stamina for one more swing? */
export function canAffordChop(stamina: number, cost: number): boolean {
  return stamina >= cost;
}

/** Spend stamina for one judged prompt; clamped at 0. */
export function afterChopSpend(stamina: number, cost: number): number {
  return Math.max(0, stamina - cost);
}
